// src/store/slices/authSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService, User } from '../../services/authService';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  resetRequestSuccess: boolean;
  resetRequestMessage: string | null;
  passwordChangeSuccess: boolean;
  passwordChangeMessage: string | null;
  passwordUpdateSuccess: boolean;
  passwordUpdateMessage: string | null;
}

const initialState: AuthState = {
  user: authService.getCurrentUser(),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,
  resetRequestSuccess: false,
  resetRequestMessage: null,
  passwordChangeSuccess: false,
  passwordChangeMessage: null,
  passwordUpdateSuccess: false,
  passwordUpdateMessage: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const user = await authService.login(credentials);
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to login');
    }
  }
);

export const requestPasswordReset = createAsyncThunk(
  'auth/requestPasswordReset',
  async (resetData: { email: string; frontend_url: string }, { rejectWithValue }) => {
    try {
      const message = await authService.requestPasswordReset(resetData);
      return message;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to request password reset');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (
    { 
      changeData, 
      token 
    }: { 
      changeData: { email: string; newPassword: string; confirmPassword: string }; 
      token: string 
    }, 
    { rejectWithValue }
  ) => {
    try {
      const message = await authService.changePassword(changeData, token);
      return message;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password');
    }
  }
);

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async (
    updateData: { oldPassword: string; newPassword: string; confirmPassword: string }, 
    { rejectWithValue }
  ) => {
    try {
      const message = await authService.updatePassword(updateData);
      return message;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update password');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    authService.logout();
    return;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearResetMessages: (state) => {
      state.resetRequestSuccess = false;
      state.resetRequestMessage = null;
    },
    clearPasswordChangeMessages: (state) => {
      state.passwordChangeSuccess = false;
      state.passwordChangeMessage = null;
    },
    clearPasswordUpdateMessages: (state) => {
      state.passwordUpdateSuccess = false;
      state.passwordUpdateMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Password reset request cases
      .addCase(requestPasswordReset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.resetRequestSuccess = false;
        state.resetRequestMessage = null;
      })
      .addCase(requestPasswordReset.fulfilled, (state, action) => {
        state.isLoading = false;
        state.resetRequestSuccess = true;
        state.resetRequestMessage = action.payload;
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.isLoading = false;
        state.resetRequestSuccess = false;
        state.error = action.payload as string;
      })
      
      // Password change cases
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordChangeSuccess = false;
        state.passwordChangeMessage = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordChangeSuccess = true;
        state.passwordChangeMessage = action.payload;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.passwordChangeSuccess = false;
        state.error = action.payload as string;
      })
      
      // Password update cases
      .addCase(updatePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordUpdateSuccess = false;
        state.passwordUpdateMessage = null;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordUpdateSuccess = true;
        state.passwordUpdateMessage = action.payload;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.passwordUpdateSuccess = false;
        state.error = action.payload as string;
      })
      
      // Logout case
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.resetRequestSuccess = false;
        state.resetRequestMessage = null;
        state.passwordChangeSuccess = false;
        state.passwordChangeMessage = null;
        state.passwordUpdateSuccess = false;
        state.passwordUpdateMessage = null;
      });
  },
});

export const { 
  clearError, 
  clearResetMessages, 
  clearPasswordChangeMessages, 
  clearPasswordUpdateMessages 
} = authSlice.actions;
export default authSlice.reducer;
